import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import { ReactComponent as RobotFace } from "../images/robotFace.svg"; // Import as React component

// Update useStyles to accept props
const useStyles = makeStyles((theme) => ({
	root: {
		position: "fixed",
		bottom: "20px",
		right: "20px",
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-end",
		zIndex: "1000", // Ensure it's above other elements
	},
	chatBtnOpen: {
		border: "none",
		background: "transparent",
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		position: "relative",
		"&:focus": {
			outline: "none",
		},
	},
	chatBtn: {
		position: "relative", // Enable absolute positioning of child elements
		background: "transparent", // Transparent background
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: "center", // Center content
		width: "60px", // **Decreased from 64px to 60px**
		height: "60px", // **Decreased from 64px to 60px**
		padding: "0",
		border: "none", // **Removed Border**
		borderRadius: "50%", // Optional: Make the button circular if desired
		"&:hover": {
			opacity: 0.9,
		},
		"&:focus": {
			outline: "none",
		},
	},
	welcomeMessage: {
		width: "fit-content",
		fontSize: "16px",
		color: "black",
		padding: "10px",
		boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
		borderRadius: "6px",
		backgroundColor: "#ffffff",
		marginBottom: "10px",
		textAlign: "center",
		[theme.breakpoints.down("xs")]: {
			fontSize: "14px",
			padding: "8px",
			marginBottom: "8px",
		},
	},
	animateSvg: {
		width: "24px",
		height: "24px",
		animation: "$fadeIn 0.5s ease-in-out",
		[theme.breakpoints.down("xs")]: {
			width: "20px",
			height: "20px",
		},
	},
	robotIcon: {
		width: "38px", // **Default Robot Face Size**
		height: "38px", // **Default Robot Face Size**
		objectFit: "cover", // Changed to 'cover' for better image sizing
		borderRadius: "50%", // Optional: Make avatar circular
		color: (props) => props.primaryColor, // **Set color based on primaryColor**
		// [theme.breakpoints.down("xs")]: {
		//   width: "25px",
		//   height: "25px",
		// },
	},
	avatarIcon: {
		width: "50px", // **Avatar Image Size**
		height: "50px", // **Avatar Image Size**
		objectFit: "cover", // Changed to 'cover' for better image sizing
		borderRadius: "50%", // Optional: Make avatar circular
		// [theme.breakpoints.down("xs")]: {
		//   width: "40px",
		//   height: "40px",
		// },
	},
	"@keyframes fadeIn": {
		"0%": { opacity: 0 },
		"100%": { opacity: 1 },
	},
	closedChat: {
		display: "flex",
		alignItems: "flex-end",
		flexDirection: "column", // Stack elements vertically
		backgroundColor: "transparent", // Transparent background for the container
		width: "100%",
	},
	bubbleContent: {
		position: "absolute", // Position absolutely within the button
		top: "45%",
		left: "52%",
		transform: "translate(-50%, -50%)", // Center horizontally and vertically
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	chatBubble: {
		width: "100%", // Expand SVG to fill the button
		height: "100%",
		// **No additional styles needed as the stroke is handled inline in SVG**
	},
}));

function ChatWindowButton({ chatOpen, toggleChatWindow, avatarUrl, primaryColor }) {
	const classes = useStyles({ primaryColor }); // Pass primaryColor to useStyles
	console.log("primaryColor", primaryColor);
	console.log("avatarUrl", avatarUrl);
	// State to manage avatar loading status
	const [isAvatarLoaded, setIsAvatarLoaded] = useState(false);
	const [isAvatarError, setIsAvatarError] = useState(false);

	return (
		<div className={classes.root}>
			{chatOpen ? (
				<button
					className={classes.chatBtnOpen}
					aria-label="Close Chat Window"
					onClick={() => toggleChatWindow()}
				>
					<svg
						aria-hidden="true"
						width="64"
						height="59"
						viewBox="0 0 64 59"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M0.886458 58.4531C8.18835 58.4531 15.3331 58.5295 22.4129 58.5172H22.4622C27.2275 58.5172 31.9287 58.4605 36.6867 58.3274C40.2514 58.2263 43.9816 57.0654 47.2899 55.5765C57.592 51.0214 63.2524 42.0344 63.7453 32.8033C63.8587 31.9949 63.9351 31.1839 63.9721 30.368C64.7166 14.2696 50.4355 0.0794349 33.679 0.0794349C26.4434 0.0695747 19.4444 2.6503 13.9444 7.35311C8.44686 12.0561 4.81541 18.5706 3.7034 25.7187C2.59404 32.8668 4.07812 40.1777 7.89186 46.325C7.84009 46.7958 7.70204 47.2519 7.48017 47.6684C5.44876 51.208 3.23989 54.6417 0.885742 58.4524L0.886458 58.4531Z"
							fill="white"
						/>
						<path
							className={classes.animateSvg}
							d="M34.6705 36.9909C35.0441 36.9492 35.3978 36.7708 35.6819 36.484L43.3189 28.8006C43.7038 28.4388 43.9468 27.9103 43.9922 27.335C44.0377 26.7597 43.8828 26.1861 43.5632 25.7476C43.2422 25.3074 42.7847 25.0406 42.2932 25.0039C41.8017 24.9689 41.3187 25.169 40.955 25.5592L34.5 32.0555L28.045 25.5592C27.6813 25.169 27.1983 24.9689 26.7068 25.0039C26.2153 25.0389 25.7578 25.3074 25.4368 25.7459C25.1172 26.1861 24.9623 26.758 25.0078 27.335C25.0532 27.9103 25.2962 28.4388 25.6811 28.8006L33.3181 36.484C33.6917 36.8609 34.1804 37.0443 34.6705 36.9909Z"
							fill="var(--active-brand-color)"
						/>
					</svg>
				</button>
			) : (
				// Closed Chat Button (Updated)
				<div className={classes.closedChat}>
					<div className={classes.welcomeMessage}>
						We're online! How can we help?
					</div>
					<button
						className={classes.chatBtn}
						aria-label="Open Chat Window"
						onClick={toggleChatWindow}
					>
						{/* Updated SVG for Closed Chat Button */}
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="-2 -2 68 68"
							className={classes.chatBubble}
						>
							<path
								d="M0.890731 58.4531C8.19263 58.4531 15.3374 58.5295 22.4171 58.5172H22.4664C27.2318 58.5172 31.933 58.4605 36.6909 58.3274C40.2557 58.2263 43.9859 57.0653 47.2941 55.5765C57.5963 51.0214 63.2567 42.0344 63.7496 32.8033C63.863 31.9949 63.9394 31.1839 63.9764 30.368C64.7209 14.2696 50.4398 0.0794349 33.6833 0.0794349C26.4477 0.0695747 19.4487 2.6503 13.9486 7.35311C8.45113 12.0561 4.81968 18.5706 3.70767 25.7187C2.59832 32.8668 4.08239 40.1777 7.89614 46.325C7.84437 46.7958 7.70631 47.2519 7.48444 47.6684C5.45304 51.208 3.24417 54.6417 0.890015 58.4524L0.890731 58.4531Z"
								fill="white"
								stroke={primaryColor} // **Dynamic Stroke Color**
								strokeWidth="2" // **2px Stroke Width**
							/>
						</svg>
						{/* Bubble Content: Avatar Image or Three Dots Animation */}
						<div className={classes.bubbleContent}>
							{/* Avatar Image */}
							{/* <img
                src={avatarUrl ? avatarUrl : robotDefault}
                alt={avatarUrl ? "User Avatar" : "Default Robot Icon"}
                className={
                  isAvatarLoaded && avatarUrl ? classes.avatarIcon : classes.robotIcon
                }
                style={{ color: primaryColor }} // **Dynamic Color for SVGs if applicable**
                onLoad={() => setIsAvatarLoaded(true)}
                onError={(e) => {
                  e.target.onerror = null; // Prevent infinite loop if fallback fails
                  setIsAvatarError(true);
                  setIsAvatarLoaded(false);
                  console.warn(
                    `Failed to load avatar from ${avatarUrl}. Falling back to default robot icon.`
                  );
                }}
              /> */}
							{avatarUrl && !isAvatarError ? (
								<img
									src={avatarUrl}
									alt="User Avatar"
									className={classes.avatarIcon}
									onLoad={() => setIsAvatarLoaded(true)}
									onError={() => setIsAvatarError(true)}
								/>
							) : (
								<RobotFace className={classes.robotIcon} />
							)}
							{/* Three Dots Animation */}
							{/* {!isAvatarLoaded && !isAvatarError && (
                <img
                  src={threeDots}
                  alt="Loading animation"
                  className={classes.animateSvg}
                />
              )} */}
						</div>
					</button>
				</div>
			)}
		</div>
	);
}

ChatWindowButton.propTypes = {
	chatOpen: PropTypes.bool.isRequired,
	toggleChatWindow: PropTypes.func.isRequired,
	avatarUrl: PropTypes.string,
	primaryColor: PropTypes.string.isRequired, // **Add primaryColor to propTypes**
};

export default ChatWindowButton;
